import VenueSelection from './VenueSelection';
import VenueAddressSearch from '../../../components/VenueAddressSearch';
import CheckboxComponent from '../../../components/CheckboxComponent';
import ContactRolesCheckbox from './ContactRolesCheckbox';
import InputField from '../../../components/InputField';
import PaymentOptions from './PaymentOptions';
import SelectedContactSummary from '../../../components/SelectedContactSummary';
import EditContactSummary from './EditContactSummary';
import AddNewContact from '../../../components/AddNewContact';
import ManualContactCard from './ManualContactCard';
import PackageCardQuestion from './PackageCardQuestion';
import CostSummary from './CostSummary';
import RegistrationLegalConfirm from './RegistrationLegalConfirm';
import InsuranceAlertSnackbar from './InsuranceAlertSnackbar';
import InsuranceRiskAssessment from './InsuranceRiskAssessment';
import { VenueRegistrationQuestionTypes } from './questionTypes';
import VenueRegistrationVenueFinder from './VenueRegistrationVenueFinder';
import { extraVenueRegistrationIntroVenueData } from './venueSearchData';

const withQuestionData = (question, commonConfig, extras = {}) => ({
	data: {
		...question,
		...extras,
		...commonConfig
	}
});

const hasContactName = (contact) => Boolean(contact?.displayName || contact?.name);

const getContactFromAnswers = (answers = {}) => {
	for (const stepAnswers of Object.values(answers)) {
		for (const answer of Object.values(stepAnswers || {})) {
			if (hasContactName(answer?.selectedContact)) {
				return answer.selectedContact;
			}
		}
	}

	return {};
};

const resolveSelectedContact = (ctx, question) => {
	const selected = ctx.data?.getSelectedContact?.();
	if (hasContactName(selected)) {
		return selected;
	}

	const fromAnswers = getContactFromAnswers(ctx.answers);
	if (hasContactName(fromAnswers)) {
		return fromAnswers;
	}

	return question.contactData || {};
};

export const venueRegistrationQuestionRenderers = {
	[VenueRegistrationQuestionTypes.PackageCard]: (_ctx, question, container, commonConfig) =>
		new PackageCardQuestion(
			container,
			withQuestionData(question, commonConfig, {
				extraSubtitle:
					question.extraSubtitle ||
					question.instructionText ||
					question.instructionText
			})
		),
	[VenueRegistrationQuestionTypes.VenueSelection]: (ctx, question, container, commonConfig) =>
		new VenueSelection(container, {
			data: {
				...question,
				...commonConfig,
				answerField: ctx.data?.radioAnswerField || question.answerField || 'id',
				valueField: question.valueField || 'value'
			}
		}),

	[VenueRegistrationQuestionTypes.VenueRegistrationVenueSearch]: (_ctx, question, container, commonConfig) =>
		new VenueRegistrationVenueFinder(
			container,
			withQuestionData(question, commonConfig, {
				...extraVenueRegistrationIntroVenueData,
				// Only send `venueType` (e.g. "park") when the step explicitly sets it - used by the
				// multisite "Add Venue" flow. The onboarding-empty-venue flow's nested venue search
				// (VenueSelection) does not set `venueType`, so it searches across all venue types.
				...(question.venueType ? { additionalParams: { venueType: question.venueType } } : {})
			})
		),

	[VenueRegistrationQuestionTypes.OrganisationAddressSearch]: (ctx, question, container, commonConfig) =>
		new VenueAddressSearch(container, {
			data: {
				...question,
				...commonConfig,
				saveAddress: ctx.data?.saveAddress ?? true,
				spacingClass: 'u-mb-2'
			}
		}),

	[VenueRegistrationQuestionTypes.DeliveryAddressSearch]: (ctx, question, container, commonConfig) =>
		new VenueAddressSearch(container, {
			data: {
				...question,
				...commonConfig,
				saveAddress: ctx.data?.saveAddress ?? true,
				spacingClass: 'u-mb-2'
			}
		}),
	[VenueRegistrationQuestionTypes.Checkbox]: (ctx, question, container, commonConfig) =>
		new CheckboxComponent(container, {
			data: {
				...question,
				...commonConfig,
				spacingClass: 'u-mb-2'
			}
		}),
	[VenueRegistrationQuestionTypes.ContactRoles]: (ctx, question, container, commonConfig) => {
		const instance = new ContactRolesCheckbox(container, {
			data: {
				...question,
				...commonConfig,
				spacingClass: 'u-mb-2',
				getContactId: () => ctx.data?.getContactId?.(),
				getSelectedContact: () => ctx.data?.getSelectedContact?.(),
				getFormAnswers: () => ctx.answers
			}
		});

		ctx.onStepChange((step) => {
			if (step === question.step) {
				instance.init();
			}
		});

		return instance;
	},
	[VenueRegistrationQuestionTypes.PaymentOptions]: (_ctx, question, container, commonConfig) =>
		new PaymentOptions(container, {
			data: {
				...question,
				...commonConfig,
				spacingClass: 'u-mb-2'
			}
		}),
	[VenueRegistrationQuestionTypes.SelectedContactSummary]: (ctx, question, container, commonConfig) => {
		const instance = new SelectedContactSummary(container, {
			data: {
				...question,
				...commonConfig,
				spacingClass: 'u-mb-3',
				shouldReRenderOnStepChange: true,
				getSelectedContact: () => ctx.data?.getSelectedContact?.() || question.contactData || {},
				contactData: question.contactData || {}
			}
		});

		if (typeof ctx.data?.onSelectedContactReset === 'function') {
			instance.onReset(ctx.data.onSelectedContactReset);
		}

		return instance;
	},
	[VenueRegistrationQuestionTypes.EditContactSummary]: (ctx, question, container, commonConfig) => {
		const instance = new EditContactSummary(container, {
			data: {
				...question,
				...commonConfig,
				spacingClass: 'u-mb-3',
				getSelectedContact: () => resolveSelectedContact(ctx, question),
				onSelectedContactReset: ctx.data?.onSelectedContactReset,
				closeOnReset: question.closeOnReset,
				closeForm: ctx.data?.exitButtonHandler || ctx.data?.exitWithoutSavingButtonHandler,
				contactData: resolveSelectedContact(ctx, question),
				shouldReRenderOnStepChange: true
			}
		});

		ctx.onStepChange((step) => {
			if (step === question.step) {
				instance.updateData({ contactData: resolveSelectedContact(ctx, question) });
			}
		});

		return instance;
	},
	[VenueRegistrationQuestionTypes.AddNewContact]: (ctx, question, container, commonConfig) =>
		new AddNewContact(container, {
			data: {
				...question,
				...commonConfig,
				spacingClass: 'u-mb-3',
				onSelectedContactChange: (contact) => ctx.data?.onSelectedContactChange?.(contact)
			}
		}),
	[VenueRegistrationQuestionTypes.ManualContactCard]: (_ctx, question, container, commonConfig) => {
		const savedContact =
			question.fullNameValue && question.emailAddressValue
				? { fullName: question.fullNameValue, email: question.emailAddressValue }
				: question.savedContact;

		return new ManualContactCard(container, {
			data: {
				...question,
				...commonConfig,
				spacingClass: 'u-mb-3',
				savedContact
			}
		});
	},
	[VenueRegistrationQuestionTypes.CostSummary]: (_ctx, question, container, commonConfig) =>
		new CostSummary(container, {
			data: {
				...question,
				...commonConfig,
				isOptional: true,
				spacingClass: 'u-mb-5'
			}
		}),

	[VenueRegistrationQuestionTypes.RegistrationLegalConfirm]: (_ctx, question, container, commonConfig) =>
		new RegistrationLegalConfirm(container, {
			data: {
				...question,
				...commonConfig,
				isOptional: true,
				spacingClass: 'u-mb-5'
			}
		}),

	[VenueRegistrationQuestionTypes.TextInput]: (_ctx, question, container, commonConfig) =>
		new InputField(container, {
			data: {
				...question,
				...commonConfig,
				isTextArea: false,
				hasCounter: false,
				spacingClass: 'u-mb-2'
			}
		}),
	[VenueRegistrationQuestionTypes.InsuranceAlertSnackbar]: (_ctx, question, container, commonConfig) =>
		new InsuranceAlertSnackbar(container, withQuestionData(question, commonConfig)),
	[VenueRegistrationQuestionTypes.InsuranceRiskAssessment]: (_ctx, question, container, commonConfig) =>
		new InsuranceRiskAssessment(container, withQuestionData(question, commonConfig))
};
